import { useRef, useCallback } from 'react';
import { LLMConfig, LLMProvider, ChatMessage, ToolCallMessage, StreamCallbacks } from '../types';
import { createLLMProvider, createLLMProviderWithConfig } from '../services/llmService';
import { getToolRegistry } from '../tools';
import { SYSTEM_PROMPT } from '../constants';

interface UseStreamingResponseOptions {
  onToken: (messageId: string, token: string) => void;
  onToolCall: (messageId: string, toolCall: ToolCallMessage) => void;
  onComplete: (messageId: string, fullResponse: string) => void;
  onError: (error: string) => void;
  systemPrompt?: string;
  configOverride?: Partial<LLMConfig>;
}

interface UseStreamingResponseReturn {
  sendMessage: (messages: ChatMessage[], messageId: string) => Promise<void>;
  cancelStream: () => void;
}

export const useStreamingResponse = (options: UseStreamingResponseOptions): UseStreamingResponseReturn => {
  const { onToken, onToolCall, onComplete, onError, systemPrompt, configOverride } = options;
  const providerRef = useRef<LLMProvider | null>(null);
  const configOverrideRef = useRef(configOverride);
  const isActiveRef = useRef(false);

  // Invalidate provider when configOverride changes
  if (JSON.stringify(configOverride) !== JSON.stringify(configOverrideRef.current)) {
    configOverrideRef.current = configOverride;
    providerRef.current = null;
  }

  const sendMessage = useCallback(
    async (messages: ChatMessage[], messageId: string): Promise<void> => {
      if (isActiveRef.current) {
        // Already processing a request - notify caller via error callback
        onError('A request is already in progress. Please wait.');
        return;
      }

      isActiveRef.current = true;

      try {
        // Create or reuse provider
        if (!providerRef.current) {
          providerRef.current = configOverrideRef.current
            ? createLLMProviderWithConfig(configOverrideRef.current)
            : createLLMProvider();
        }

        const provider = providerRef.current;
        const toolRegistry = getToolRegistry();

        const callbacks: StreamCallbacks = {
          onToken: (token) => {
            onToken(messageId, token);
          },
          onToolCall: (toolCall) => {
            onToolCall(messageId, toolCall);
          },
          onComplete: (fullResponse) => {
            onComplete(messageId, fullResponse);

            // If there are pending tool calls, they will be handled by the chat orchestrator
            // The completion here is just for the streaming portion
          },
          onError: (error) => {
            onError(error.message);
          },
        };

        await provider.chat(
          {
            messages,
            systemPrompt: systemPrompt || SYSTEM_PROMPT,
            tools: toolRegistry.getSchemas(),
          },
          callbacks,
        );
      } catch (error) {
        onError((error as Error).message);
      } finally {
        isActiveRef.current = false;
      }
    },
    [onToken, onToolCall, onComplete, onError, systemPrompt],
  );

  const cancelStream = useCallback((): void => {
    if (providerRef.current && isActiveRef.current) {
      providerRef.current.cancelRequest();
      isActiveRef.current = false;
    }
  }, []);

  return {
    sendMessage,
    cancelStream,
  };
};
